import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Clock,
  Plus,
  Play,
  Edit2,
  Trash2,
  Power,
  RefreshCcw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Shield,
  FileText,
  X,
  BookOpen
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import CustomDropdown from "../components/CustomDropdown";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const MINUTE_PRESETS = [
  { id: "1", name: "Every 1 minute (Demo mode)" },
  { id: "5", name: "Every 5 minutes" },
  { id: "15", name: "Every 15 minutes" },
  { id: "30", name: "Every 30 minutes" },
  { id: "45", name: "Every 45 minutes" },
  { id: "60", name: "Every 60 minutes" },
  { id: "custom", name: "Custom minutes..." }
];

const HOUR_PRESETS = [
  { id: "1", name: "Every 1 hour" },
  { id: "2", name: "Every 2 hours" },
  { id: "4", name: "Every 4 hours" },
  { id: "6", name: "Every 6 hours" },
  { id: "12", name: "Every 12 hours" },
  { id: "24", name: "Every 24 hours" },
  { id: "custom", name: "Custom hours..." }
];

const DAILY_PRESETS = [
  { id: "08:00 AM", name: "08:00 AM" },
  { id: "09:00 AM", name: "09:00 AM" },
  { id: "10:00 AM", name: "10:00 AM" },
  { id: "12:00 PM", name: "12:00 PM" },
  { id: "04:00 PM", name: "04:00 PM" },
  { id: "08:00 PM", name: "08:00 PM" },
  { id: "custom", name: "Custom time..." }
];

const to24Hour = (timeStr) => {
  if (!timeStr) return "10:00";
  const str = String(timeStr).trim();
  const match = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) return str.length === 5 ? str : "10:00";
  let [, h, m, mer] = match;
  let hour = parseInt(h, 10);
  if (mer) {
    if (mer.toUpperCase() === "PM" && hour < 12) hour += 12;
    if (mer.toUpperCase() === "AM" && hour === 12) hour = 0;
  }
  return `${String(hour).padStart(2, "0")}:${m}`;
};

const to12Hour = (timeStr) => {
  if (!timeStr) return "10:00 AM";
  const str = String(timeStr).trim();
  const match = str.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!match) return timeStr;
  let [, h, m, mer] = match;
  if (mer) return `${String(parseInt(h, 10)).padStart(2, "0")}:${m} ${mer.toUpperCase()}`;
  let hour = parseInt(h, 10);
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${m} ${suffix}`;
};

const ScheduleFrequencyPicker = ({ scheduleType = "hours", scheduleValue = "2", onChange }) => {
  const [isCustom, setIsCustom] = useState(false);

  const currentType = scheduleType || "hours";
  const rawVal = String(scheduleValue || "").trim();

  useEffect(() => {
    if (currentType === "minutes") {
      const cleanMin = rawVal.replace(/mins|min|m/gi, "").trim();
      const matched = MINUTE_PRESETS.some((p) => p.id === cleanMin && p.id !== "custom");
      setIsCustom(!matched || cleanMin === "custom");
    } else if (currentType === "hours") {
      const cleanHr = rawVal.replace("h", "").trim();
      const matched = HOUR_PRESETS.some((p) => p.id === cleanHr && p.id !== "custom");
      setIsCustom(!matched || cleanHr === "custom");
    } else if (currentType === "daily") {
      const norm12 = to12Hour(rawVal);
      const matched = DAILY_PRESETS.some((p) => p.id === norm12 && p.id !== "custom");
      setIsCustom(!matched);
    }
  }, [currentType, rawVal]);

  const handleTypeChange = (newType) => {
    setIsCustom(false);
    if (newType === "minutes") {
      onChange("minutes", "15");
    } else if (newType === "hours") {
      onChange("hours", "2");
    } else {
      onChange("daily", "10:00 AM");
    }
  };

  const handlePresetSelect = (presetId) => {
    if (presetId === "custom") {
      setIsCustom(true);
      if (currentType === "minutes") {
        onChange("minutes", rawVal && rawVal !== "custom" ? rawVal : "54");
      } else if (currentType === "hours") {
        onChange("hours", rawVal && rawVal !== "custom" ? rawVal : "3");
      } else {
        onChange("daily", rawVal && rawVal !== "custom" ? rawVal : "10:00 AM");
      }
    } else {
      setIsCustom(false);
      onChange(currentType, presetId);
    }
  };

  let selectedDropdownId = "custom";
  if (!isCustom) {
    if (currentType === "minutes") {
      const cleanMin = rawVal.replace(/mins|min|m/gi, "").trim();
      if (MINUTE_PRESETS.some((p) => p.id === cleanMin)) selectedDropdownId = cleanMin;
    } else if (currentType === "hours") {
      const cleanHr = rawVal.replace("h", "").trim();
      if (HOUR_PRESETS.some((p) => p.id === cleanHr)) selectedDropdownId = cleanHr;
    } else if (currentType === "daily") {
      const norm12 = to12Hour(rawVal);
      if (DAILY_PRESETS.some((p) => p.id === norm12)) selectedDropdownId = norm12;
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] text-zinc-500 block mb-1">Schedule Type</label>
          <CustomDropdown
            options={[
              { id: "minutes", name: "Every X Minutes" },
              { id: "hours", name: "Every X Hours" },
              { id: "daily", name: "Daily" }
            ]}
            value={currentType}
            onChange={handleTypeChange}
            buttonClassName="w-full px-3 py-2 border border-zinc-200 rounded-xl text-xs hover:border-zinc-900 bg-white"
          />
        </div>

        <div>
          <label className="text-[11px] text-zinc-500 block mb-1">
            {currentType === "daily" ? "Run Time" : "Interval"}
          </label>
          <CustomDropdown
            options={
              currentType === "minutes"
                ? MINUTE_PRESETS
                : currentType === "hours"
                ? HOUR_PRESETS
                : DAILY_PRESETS
            }
            value={selectedDropdownId}
            onChange={handlePresetSelect}
            buttonClassName="w-full px-3 py-2 border border-zinc-200 rounded-xl text-xs hover:border-zinc-900 bg-white"
          />
        </div>
      </div>

      {isCustom && (
        <div className="p-3 bg-zinc-50 border border-zinc-200/90 rounded-xl space-y-2 animate-fadeIn">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-zinc-700">
              {currentType === "minutes"
                ? "Custom Minute Interval"
                : currentType === "hours"
                ? "Custom Hour Interval"
                : "Custom Daily Run Time"}
            </span>
            <span className="text-[10px] text-zinc-500 font-mono">
              {currentType === "minutes"
                ? `Every ${rawVal.replace(/mins|min|m/gi, "") || 1} min`
                : currentType === "hours"
                ? `Every ${rawVal} hr`
                : `Daily at ${to12Hour(rawVal)}`}
            </span>
          </div>

          {currentType === "minutes" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="1440"
                  value={rawVal.replace(/mins|min|m/gi, "")}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    onChange("minutes", val || "1");
                  }}
                  placeholder="e.g. 54"
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 bg-white font-mono"
                />
                <span className="text-xs font-semibold text-zinc-600 shrink-0">minutes</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] text-zinc-400">Quick set:</span>
                {["10", "20", "45", "54", "90"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => onChange("minutes", m)}
                    className={`px-2 py-0.5 text-[10px] rounded-md border transition-all ${
                      rawVal.replace(/mins|min|m/gi, "") === m
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    {m}m
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentType === "hours" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="720"
                  step="any"
                  value={rawVal}
                  onChange={(e) => onChange("hours", e.target.value || "1")}
                  placeholder="e.g. 3 or 8"
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 bg-white font-mono"
                />
                <span className="text-xs font-semibold text-zinc-600 shrink-0">hours</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] text-zinc-400">Quick set:</span>
                {["3", "5", "8", "10", "18"].map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => onChange("hours", h)}
                    className={`px-2 py-0.5 text-[10px] rounded-md border transition-all ${
                      rawVal === h
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentType === "daily" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={to24Hour(rawVal)}
                  onChange={(e) => {
                    const time24 = e.target.value;
                    if (time24) {
                      onChange("daily", to12Hour(time24));
                    }
                  }}
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 bg-white font-mono"
                />
                <span className="text-xs font-semibold text-zinc-600 shrink-0">
                  {to12Hour(rawVal)}
                </span>
              </div>
              <p className="text-[11px] text-zinc-400">
                Sync executes once every day at this exact time (Asia/Kolkata).
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const KnowledgeBaseCron = ({ activeAiAgentId, onSyncSuccess }) => {
  const { showToast } = useToast();
  const [crons, setCrons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isTypeSelectorOpen, setIsTypeSelectorOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFreshdeskModalOpen, setIsFreshdeskModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [editingCron, setEditingCron] = useState(null);
  const [activeHistoryCron, setActiveHistoryCron] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [runningCronId, setRunningCronId] = useState(null);
  const [runProgress, setRunProgress] = useState(null); // { cronId, stepText }

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [cronToDelete, setCronToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Normal Form State
  const [formData, setFormData] = useState({
    name: "Document Repository Sync",
    api_url: "http://localhost:8001/api/documents",
    http_method: "GET",
    authentication_type: "none",
    credentials: "",
    schedule_type: "hours",
    schedule_value: "2",
    folder_path: "/",
    repository: "External Sync Repo",
    status: "active"
  });

  // Freshdesk Integration State
  const [freshdeskStatus, setFreshdeskStatus] = useState({
    loading: false,
    connected: false,
    domain: "",
    categories: [],
    folders: []
  });
  const [freshdeskForm, setFreshdeskForm] = useState({
    name: "Freshdesk Knowledge Base Sync",
    sync_scope: "all", // "all", "category", "folder", "article"
    scope_value: "",
    repository: "Freshdesk KB",
    folder_path: "/freshdesk",
    schedule_type: "hours",
    schedule_value: "2",
    freshdesk_domain: "",
    api_key: "",
    status: "active"
  });

  const fetchCrons = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await ApiClient.get("/api/knowledge-base/cron");
      setCrons(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Failed to load KB crons:", err);
      if (!silent) showToast("Failed to load Cron configurations", "error");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeAiAgentId) return;
    fetchCrons(false);
    // Silent background refresh every 10s: no spinner, zero flicker/blinking
    const pollInterval = setInterval(() => {
      fetchCrons(true);
    }, 10000);
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAiAgentId]);

  const fetchFreshdeskStatus = async () => {
    try {
      setFreshdeskStatus(prev => ({ ...prev, loading: true }));
      const res = await ApiClient.get("/api/knowledge-base/cron/freshdesk/connection-status");
      if (res) {
        setFreshdeskStatus({
          loading: false,
          connected: Boolean(res.connected),
          domain: res.domain || "",
          categories: res.categories || [],
          folders: res.folders || []
        });
        if (res.domain) {
          setFreshdeskForm(prev => ({
            ...prev,
            freshdesk_domain: res.domain
          }));
        }
      }
    } catch (err) {
      console.error("Failed to check Freshdesk status:", err);
      setFreshdeskStatus({
        loading: false,
        connected: false,
        domain: "",
        categories: [],
        folders: []
      });
    }
  };

  const handleOpenAdd = () => {
    setIsTypeSelectorOpen(true);
  };

  const handleOpenEdit = (cron) => {
    setEditingCron(cron);
    if (cron.cron_type === "integration") {
      setFreshdeskForm({
        name: cron.name || "Freshdesk Knowledge Base Sync",
        sync_scope: cron.sync_scope || "all",
        scope_value: cron.scope_value || "",
        repository: cron.repository || "Freshdesk KB",
        folder_path: cron.folder_path || "/freshdesk",
        schedule_type: cron.schedule_type || "hours",
        schedule_value: cron.schedule_value || "2",
        freshdesk_domain: "",
        api_key: "",
        status: cron.status || "active"
      });
      fetchFreshdeskStatus();
      setIsFreshdeskModalOpen(true);
    } else {
      setFormData({
        name: cron.name || "",
        api_url: cron.api_url || "",
        http_method: cron.http_method || "GET",
        authentication_type: cron.authentication_type || "none",
        credentials: "", // never exposed back to UI for security
        schedule_type: cron.schedule_type || "hours",
        schedule_value: cron.schedule_value || "2",
        folder_path: cron.folder_path || "/",
        repository: cron.repository || "External Sync Repo",
        status: cron.status || "active"
      });
      setIsModalOpen(true);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.api_url.trim()) {
      showToast("Please enter a name and API endpoint", "warning");
      return;
    }

    try {
      if (editingCron) {
        const payload = { ...formData, cron_type: "normal" };
        if (!payload.credentials) delete payload.credentials; // don't overwrite if empty
        await ApiClient.put(`/api/knowledge-base/cron/${editingCron.id}`, payload);
        showToast("Cron configuration updated successfully", "success");
      } else {
        await ApiClient.post("/api/knowledge-base/cron", { ...formData, cron_type: "normal" });
        showToast("Cron configuration added successfully", "success");
      }
      setIsModalOpen(false);
      fetchCrons();
    } catch (err) {
      console.error("Save cron failed:", err);
      showToast("Failed to save Cron configuration", "error");
    }
  };

  const handleSaveFreshdeskCron = async (e) => {
    e.preventDefault();
    if (!freshdeskForm.name.trim()) {
      showToast("Please enter a schedule name", "warning");
      return;
    }
    if (freshdeskForm.sync_scope !== "all" && !freshdeskForm.scope_value.trim()) {
      showToast(`Please enter or select a ${freshdeskForm.sync_scope} identifier`, "warning");
      return;
    }
    if (!freshdeskStatus.connected && (!freshdeskForm.freshdesk_domain.trim() || !freshdeskForm.api_key.trim())) {
      showToast("Please provide Freshdesk domain and API key", "warning");
      return;
    }

    try {
      const payload = {
        name: freshdeskForm.name.trim(),
        cron_type: "integration",
        integration_type: "freshdesk",
        sync_scope: freshdeskForm.sync_scope,
        scope_value: freshdeskForm.sync_scope === "all" ? "" : freshdeskForm.scope_value.trim(),
        repository: freshdeskForm.repository.trim() || "Freshdesk KB",
        folder_path: freshdeskForm.folder_path.trim() || "/freshdesk",
        schedule_type: freshdeskForm.schedule_type,
        schedule_value: freshdeskForm.schedule_value,
        status: freshdeskForm.status
      };

      if (!freshdeskStatus.connected && freshdeskForm.freshdesk_domain) {
        const cleanDomain = freshdeskForm.freshdesk_domain.replace(/^https?:\/\//, "").replace(/\/$/, "");
        payload.api_url = `https://${cleanDomain}/api/v2/solutions`;
        payload.authentication_type = "basic";
        payload.credentials = freshdeskForm.api_key;
      } else {
        const domain = freshdeskStatus.domain || "freshdesk";
        payload.api_url = `https://${domain}/api/v2/solutions`;
        payload.authentication_type = "basic";
      }

      if (editingCron) {
        if (!freshdeskForm.api_key) {
          delete payload.credentials;
        }
        await ApiClient.put(`/api/knowledge-base/cron/${editingCron.id}`, payload);
        showToast("Freshdesk integration cron updated successfully", "success");
      } else {
        await ApiClient.post("/api/knowledge-base/cron", payload);
        showToast("Freshdesk integration cron created successfully", "success");
      }
      setIsFreshdeskModalOpen(false);
      fetchCrons();
    } catch (err) {
      console.error("Save Freshdesk cron failed:", err);
      showToast("Failed to save Freshdesk Cron configuration", "error");
    }
  };

  const handleToggleStatus = async (cron) => {
    const newStatus = cron.status === "active" ? "inactive" : "active";
    try {
      await ApiClient.put(`/api/knowledge-base/cron/${cron.id}`, { status: newStatus });
      showToast(`Cron ${newStatus === "active" ? "enabled" : "disabled"}`, "success");
      setCrons(prev => prev.map(c => c.id === cron.id ? { ...c, status: newStatus } : c));
    } catch (err) {
      showToast("Failed to update status", "error");
    }
  };

  const handleRunNow = async (cron) => {
    if (runningCronId) return;
    setRunningCronId(cron.id);

    // Dynamic UI Progress steps
    setRunProgress({ cronId: cron.id, text: "Checking API endpoint..." });

    try {
      setTimeout(() => {
        setRunProgress({ cronId: cron.id, text: "Comparing documents with Knowledge Base..." });
      }, 500);

      const res = await ApiClient.post(`/api/knowledge-base/cron/${cron.id}/run-now`, {});

      setTimeout(() => {
        if (res.new_documents_count > 0) {
          setRunProgress({
            cronId: cron.id,
            text: `Imported ${res.new_documents_count} new document(s) & vectorized!`
          });
        } else {
          setRunProgress({ cronId: cron.id, text: "Sync completed. No new documents found." });
        }
      }, 900);

      setTimeout(() => {
        setRunningCronId(null);
        setRunProgress(null);
        fetchCrons();
        if (onSyncSuccess) onSyncSuccess();
        if (res.success) {
          showToast(res.message || "Sync completed successfully", "success");
        } else {
          showToast(res.message || "Sync encountered an error", "error");
        }
      }, 1600);
    } catch (err) {
      setRunningCronId(null);
      setRunProgress(null);
      fetchCrons();
      showToast("Failed to trigger sync: API connection issue", "error");
    }
  };

  const handleOpenHistory = async (cron) => {
    setActiveHistoryCron(cron);
    setIsHistoryModalOpen(true);
    setLoadingHistory(true);
    try {
      const res = await ApiClient.get(`/api/knowledge-base/cron/${cron.id}/history`);
      setSyncHistory(Array.isArray(res) ? res : []);
    } catch (err) {
      showToast("Failed to load sync history", "error");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDeleteClick = (cron) => {
    setCronToDelete(cron);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!cronToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.delete(`/api/knowledge-base/cron/${cronToDelete.id}`);
      showToast("Cron schedule deleted", "success");
      setIsDeleteModalOpen(false);
      setCronToDelete(null);
      fetchCrons();
    } catch (err) {
      showToast("Failed to delete cron", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatScheduleText = (type, val) => {
    if (!type && !val) return "Every 2 hours";
    const strVal = String(val || "").trim();
    if (type === "daily") {
      return `Daily at ${to12Hour(strVal)}`;
    }
    if (type === "minutes" || strVal.endsWith("m") || strVal.endsWith("min") || strVal.endsWith("mins")) {
      const clean = strVal.replace(/mins|min|m/gi, "").trim() || "1";
      if (clean === "1") return "Every 1 minute (Demo mode)";
      return `Every ${clean} minutes`;
    }
    return `Every ${strVal} hour${strVal === "1" ? "" : "s"}`;
  };

  const formatDateDisplay = (dtStr) => {
    if (!dtStr) return "Never";
    // Check if timestamp is UTC or naive; ensure proper parsing
    const d = new Date(dtStr.endsWith && !dtStr.endsWith("Z") ? dtStr + "Z" : dtStr);
    if (isNaN(d.getTime())) return dtStr;
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    }) + " (IST)";
  };

  return (
    <div className="space-y-6">
      {/* Top Header & Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-zinc-100">
        <div>
          <h2 className="text-sm sm:text-base font-semibold text-zinc-900 tracking-tight flex items-center gap-2">
            <Clock size={16} className="text-zinc-600" />
            Document Synchronizations
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Automatically poll external document APIs and incrementally import only newly published documents.
          </p>
        </div>
        <button
          onClick={handleOpenAdd}
          className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800 transition-colors shadow-xs active:scale-95"
        >
          <Plus size={14} />
          <span>Add Cron</span>
        </button>
      </div>

      {/* Main List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-400">
          <RefreshCcw size={22} className="animate-spin mb-2" />
          <span className="text-xs font-medium">Loading sync schedules...</span>
        </div>
      ) : crons.length === 0 ? (
        <div className="bg-white border border-zinc-200/80 rounded-2xl p-8 sm:p-12 text-center shadow-xs">
          <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-3 text-zinc-400">
            <Clock size={24} />
          </div>
          <h3 className="text-sm font-semibold text-zinc-900 mb-1">No Cron Schedules Configured</h3>
          <p className="text-xs text-zinc-500 max-w-sm mx-auto mb-5 leading-relaxed">
            Set up an automated periodic sync to fetch new documents from your external CMS, Google Drive, or repository API.
          </p>
          <button
            onClick={handleOpenAdd}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800 transition-colors"
          >
            <Plus size={14} />
            Configure Your First Cron
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {crons.map((cron) => {
            const isRunning = runningCronId === cron.id;
            const progressInfo = runProgress && runProgress.cronId === cron.id ? runProgress.text : null;

            return (
              <div
                key={cron.id}
                className="bg-white border border-zinc-200/80 rounded-2xl p-5 sm:p-6 shadow-xs hover:border-zinc-300 transition-all space-y-4"
              >
                {/* Header row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-zinc-100 border border-zinc-200/60 flex items-center justify-center text-zinc-700 shrink-0">
                      <Clock size={16} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-zinc-900">{cron.name}</h4>
                        {cron.cron_type === "integration" && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-900 text-white">
                            Freshdesk
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            cron.status === "active"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-zinc-100 text-zinc-500 border border-zinc-200"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              cron.status === "active" ? "bg-emerald-500" : "bg-zinc-400"
                            }`}
                          />
                          {cron.status === "active" ? "Active" : "Disabled"}
                        </span>
                      </div>
                      {cron.cron_type === "integration" ? (
                        <p className="text-xs text-zinc-500 font-medium mt-0.5 truncate max-w-md flex items-center gap-1.5">
                          <span className="text-[11px] font-semibold text-zinc-700 uppercase tracking-wider">Scope:</span>
                          <span className="text-zinc-600 font-medium">
                            {cron.sync_scope === "article"
                              ? "Specific Article"
                              : cron.sync_scope === "category"
                              ? "Category"
                              : cron.sync_scope === "folder"
                              ? "Folder"
                              : "All Articles"}
                          </span>
                          {cron.scope_value && (
                            <span className="font-mono text-zinc-700 bg-zinc-100 px-1.5 py-0.5 rounded text-[11px] border border-zinc-200/60">
                              {cron.scope_value}
                            </span>
                          )}
                        </p>
                      ) : (
                        <p className="text-xs text-zinc-500 font-mono mt-0.5 truncate max-w-md">
                          {cron.http_method} {cron.api_url}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <button
                      onClick={() => handleRunNow(cron)}
                      disabled={isRunning}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-xs active:scale-95"
                      title="Trigger sync immediately"
                    >
                      {isRunning ? (
                        <RefreshCcw size={12} className="animate-spin text-white" />
                      ) : (
                        <Play size={12} />
                      )}
                      <span>{isRunning ? "Syncing..." : "Run Now"}</span>
                    </button>
                    <button
                      onClick={() => handleOpenEdit(cron)}
                      className="p-1.5 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
                      title="Edit Cron"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleOpenHistory(cron)}
                      className="p-1.5 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
                      title="View Sync History"
                    >
                      <FileText size={14} />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(cron)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        cron.status === "active"
                          ? "text-emerald-600 hover:bg-emerald-50"
                          : "text-zinc-400 hover:bg-zinc-100"
                      }`}
                      title={cron.status === "active" ? "Disable Cron" : "Enable Cron"}
                    >
                      <Power size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(cron)}
                      className="p-1.5 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                      title="Delete Cron"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Real-time status / progress banner */}
                {progressInfo && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 text-blue-700 rounded-xl text-xs font-medium animate-pulse">
                    <RefreshCcw size={13} className="animate-spin shrink-0" />
                    <span>{progressInfo}</span>
                  </div>
                )}

                {/* Details Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-zinc-100 text-xs">
                  <div>
                    <span className="text-[10px] font-medium text-zinc-400 block mb-0.5">Schedule</span>
                    <span className="font-semibold text-zinc-800">
                      {formatScheduleText(cron.schedule_type, cron.schedule_value)}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] font-medium text-zinc-400 block mb-0.5">Last Sync</span>
                    <span className="font-semibold text-zinc-800">
                      {formatDateDisplay(cron.last_run_at)}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] font-medium text-zinc-400 block mb-0.5">Last Result</span>
                    <span
                      className={`font-semibold flex items-center gap-1 ${
                        cron.last_run_status === "failed"
                          ? "text-rose-600"
                          : cron.last_run_result?.includes("new document")
                          ? "text-emerald-600"
                          : "text-zinc-700"
                      }`}
                    >
                      {cron.last_run_status === "failed" ? (
                        <XCircle size={12} />
                      ) : cron.last_run_status === "success" ? (
                        <CheckCircle2 size={12} />
                      ) : null}
                      {cron.last_run_result || "Not run yet"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] font-medium text-zinc-400 block mb-0.5">Next Sync</span>
                    <span className="font-semibold text-zinc-800">
                      {cron.status === "active" ? formatDateDisplay(cron.next_run_at) : "Paused"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Select Cron Type Selector Modal (Monochromatic neutral borders) */}
      {isTypeSelectorOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-xs animate-fadeIn font-['Inter',sans-serif]">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-zinc-200 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-100">
              <div>
                <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                  <Clock size={16} className="text-zinc-600" />
                  Add Cron
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">Select Cron Type</p>
              </div>
              <button
                onClick={() => setIsTypeSelectorOpen(false)}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-6 space-y-3">
              {/* Option 1: Normal */}
              <div
                onClick={() => {
                  setIsTypeSelectorOpen(false);
                  setEditingCron(null);
                  setFormData({
                    name: "Document Repository Sync",
                    api_url: "http://localhost:8001/api/documents",
                    http_method: "GET",
                    authentication_type: "none",
                    credentials: "",
                    schedule_type: "hours",
                    schedule_value: "2",
                    folder_path: "/",
                    repository: "External Sync Repo",
                    status: "active"
                  });
                  setIsModalOpen(true);
                }}
                className="group border border-zinc-200/90 hover:border-zinc-900 rounded-xl p-4 cursor-pointer transition-all bg-white hover:bg-zinc-50/60 shadow-xs"
              >
                <div className="flex items-start gap-3.5">
                  <div className="w-9 h-9 rounded-xl bg-zinc-100 border border-zinc-200/70 flex items-center justify-center text-zinc-700 shrink-0 mt-0.5 group-hover:bg-zinc-900 group-hover:text-white transition-all">
                    <Clock size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold text-zinc-900">Normal</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600 border border-zinc-200/80">
                        Existing flow
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      Continue the existing custom API Cron flow without changing its behavior.
                    </p>
                  </div>
                </div>
              </div>

              {/* Option 2: Integration */}
              <div
                onClick={() => {
                  setIsTypeSelectorOpen(false);
                  setEditingCron(null);
                  setFreshdeskForm({
                    name: "Freshdesk Knowledge Base Sync",
                    sync_scope: "all",
                    scope_value: "",
                    repository: "Freshdesk KB",
                    folder_path: "/freshdesk",
                    schedule_type: "hours",
                    schedule_value: "2",
                    freshdesk_domain: "",
                    api_key: "",
                    status: "active"
                  });
                  fetchFreshdeskStatus();
                  setIsFreshdeskModalOpen(true);
                }}
                className="group border border-zinc-200/90 hover:border-zinc-900 rounded-xl p-4 cursor-pointer transition-all bg-white hover:bg-zinc-50/60 shadow-xs"
              >
                <div className="flex items-start gap-3.5">
                  <div className="w-9 h-9 rounded-xl bg-zinc-900 text-white flex items-center justify-center shrink-0 mt-0.5 transition-all">
                    <BookOpen size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold text-zinc-900">Integration</span>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-zinc-900 text-white">
                        Freshdesk
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      Connect a platform and sync its knowledge-base articles.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-zinc-100 bg-zinc-50/40 flex justify-end">
              <button
                type="button"
                onClick={() => setIsTypeSelectorOpen(false)}
                className="px-4 py-2 border border-zinc-200/90 text-zinc-700 rounded-xl text-xs font-semibold hover:bg-zinc-100 transition-colors shadow-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Freshdesk Integration Configuration Modal */}
      {isFreshdeskModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-xs animate-fadeIn font-['Inter',sans-serif]">
          <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl border border-zinc-200 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-5 border-b border-zinc-100">
              <div>
                <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                  <BookOpen size={16} />
                  Freshdesk Knowledge Base Sync
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Schedule automated article synchronization with ID-level deduplication
                </p>
              </div>
              <button
                onClick={() => setIsFreshdeskModalOpen(false)}
                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveFreshdeskCron} className="p-5 space-y-4 overflow-y-auto">
              {/* Account Connection Status */}
              <div className="p-3.5 rounded-xl border border-zinc-200 bg-zinc-50/70">
                {freshdeskStatus.loading ? (
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <RefreshCcw size={14} className="animate-spin text-zinc-600" />
                    <span>Verifying Freshdesk connection...</span>
                  </div>
                ) : freshdeskStatus.connected ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-zinc-900">Freshdesk</span>
                        <span className="font-mono text-[11px] text-zinc-600 bg-white px-2 py-0.5 rounded border border-zinc-200">
                          {freshdeskStatus.domain}
                        </span>
                      </div>
                      <p className="text-[11px] text-zinc-500">
                        Using authorized account credentials from Integrations.
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Connected
                    </span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-zinc-800 text-xs font-semibold">
                        <AlertCircle size={14} className="text-amber-600" />
                        <span>Freshdesk Not Connected</span>
                      </div>
                      <span className="text-[10px] text-zinc-400">Inline Setup</span>
                    </div>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                      Enter your Freshdesk domain and API key below, or configure it under Settings &gt; Integrations.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <div>
                        <label className="text-[11px] font-semibold text-zinc-700 block mb-1">Domain</label>
                        <input
                          type="text"
                          required={!freshdeskStatus.connected}
                          value={freshdeskForm.freshdesk_domain}
                          onChange={(e) => setFreshdeskForm({ ...freshdeskForm, freshdesk_domain: e.target.value })}
                          placeholder="company.freshdesk.com"
                          className="w-full px-3 py-1.5 text-xs border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold text-zinc-700 block mb-1">API Key</label>
                        <input
                          type="password"
                          required={!freshdeskStatus.connected}
                          value={freshdeskForm.api_key}
                          onChange={(e) => setFreshdeskForm({ ...freshdeskForm, api_key: e.target.value })}
                          placeholder="Freshdesk API Key"
                          className="w-full px-3 py-1.5 text-xs border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 font-mono"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Schedule Name */}
              <div>
                <label className="text-xs font-semibold text-zinc-700 block mb-1">Schedule Name</label>
                <input
                  type="text"
                  required
                  value={freshdeskForm.name}
                  onChange={(e) => setFreshdeskForm({ ...freshdeskForm, name: e.target.value })}
                  placeholder="e.g. Freshdesk Knowledge Base Sync"
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900"
                />
              </div>

              {/* Section: ARTICLE SELECTION */}
              <div className="pt-3 border-t border-zinc-100 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider">
                    Article Selection
                  </span>
                  <span className="text-[10px] text-zinc-400">Import scope</span>
                </div>

                {/* Scope selector tabs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { id: "all", label: "All articles", desc: "Across helpdesk" },
                    { id: "category", label: "Category", desc: "Category & folders" },
                    { id: "folder", label: "Folder", desc: "Single folder" },
                    { id: "article", label: "Specific article", desc: "By ID or title" }
                  ].map((scope) => {
                    const isSelected = freshdeskForm.sync_scope === scope.id;
                    return (
                      <button
                        key={scope.id}
                        type="button"
                        onClick={() =>
                          setFreshdeskForm({
                            ...freshdeskForm,
                            sync_scope: scope.id,
                            scope_value: scope.id === "all" ? "" : freshdeskForm.scope_value
                          })
                        }
                        className={`p-2.5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? "bg-zinc-900 text-white border-zinc-900 shadow-xs"
                            : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
                        }`}
                      >
                        <div className="text-xs font-semibold">{scope.label}</div>
                        <div className={`text-[10px] mt-0.5 truncate ${isSelected ? "text-zinc-300" : "text-zinc-400"}`}>
                          {scope.desc}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Scope specific inputs */}
                {freshdeskForm.sync_scope === "all" && (
                  <p className="text-[11px] text-zinc-500 bg-zinc-50 p-2.5 rounded-lg border border-zinc-100">
                    Discover and sync all solution articles across the connected Freshdesk knowledge base. Incremental: existing article IDs are skipped.
                  </p>
                )}

                {freshdeskForm.sync_scope === "category" && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-zinc-700 block">
                        Category
                      </label>
                      {freshdeskStatus.categories && freshdeskStatus.categories.length > 0 ? (
                        <CustomDropdown
                          options={freshdeskStatus.categories.map((c) => ({
                            id: c.name,
                            name: `${c.name} (ID: ${c.id})`
                          }))}
                          value={freshdeskForm.scope_value}
                          onChange={(val) => setFreshdeskForm({ ...freshdeskForm, scope_value: val })}
                          placeholder="Select discovered category..."
                          buttonClassName="w-full px-3 py-2 border border-zinc-200 rounded-xl text-xs hover:border-zinc-900 bg-white"
                        />
                      ) : (
                        <p className="text-[11px] text-zinc-400">No categories found in connected Freshdesk.</p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] text-zinc-400 block">
                        Or enter Category Name / ID manually:
                      </label>
                      <input
                        type="text"
                        required
                        value={freshdeskForm.scope_value}
                        onChange={(e) => setFreshdeskForm({ ...freshdeskForm, scope_value: e.target.value })}
                        placeholder="e.g. Troubleshooting or Category ID (e.g. 103000000123)"
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 bg-white"
                      />
                    </div>
                    <p className="text-[11px] text-zinc-400">
                      Imports articles in this category, including all its folders and nested folders.
                    </p>
                  </div>
                )}

                {freshdeskForm.sync_scope === "folder" && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-zinc-700 block">
                        Folder
                      </label>
                      {freshdeskStatus.folders && freshdeskStatus.folders.length > 0 ? (
                        <CustomDropdown
                          options={freshdeskStatus.folders.map((f) => ({
                            id: f.name,
                            name: `${f.name} (ID: ${f.id}) — in ${f.category_name}`
                          }))}
                          value={freshdeskForm.scope_value}
                          onChange={(val) => setFreshdeskForm({ ...freshdeskForm, scope_value: val })}
                          placeholder="Select discovered folder..."
                          buttonClassName="w-full px-3 py-2 border border-zinc-200 rounded-xl text-xs hover:border-zinc-900 bg-white"
                        />
                      ) : (
                        <p className="text-[11px] text-zinc-400">No folders found in connected Freshdesk.</p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] text-zinc-400 block">
                        Or enter Folder Name / ID manually:
                      </label>
                      <input
                        type="text"
                        required
                        value={freshdeskForm.scope_value}
                        onChange={(e) => setFreshdeskForm({ ...freshdeskForm, scope_value: e.target.value })}
                        placeholder="e.g. Getting Started or Folder ID (e.g. 103000000456)"
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 bg-white"
                      />
                    </div>
                    <p className="text-[11px] text-zinc-400">
                      Imports articles from this folder and optionally its subfolders.
                    </p>
                  </div>
                )}

                {freshdeskForm.sync_scope === "article" && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-zinc-700 block">
                      Article ID or Title
                    </label>
                    <input
                      type="text"
                      required
                      value={freshdeskForm.scope_value}
                      onChange={(e) => setFreshdeskForm({ ...freshdeskForm, scope_value: e.target.value })}
                      placeholder="e.g. 10001 or 'How to reset password'"
                      className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 font-mono"
                    />
                    <p className="text-[11px] text-zinc-400">
                      Fetches one article directly using its ID or title keyword without traversing the hierarchy.
                    </p>
                  </div>
                )}
              </div>

              {/* Section: SYNC SETTINGS */}
              <div className="pt-3 border-t border-zinc-100 space-y-3">
                <span className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider block">
                  Sync Settings
                </span>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-zinc-700 block mb-1">Target Repository</label>
                    <input
                      type="text"
                      value={freshdeskForm.repository}
                      onChange={(e) => setFreshdeskForm({ ...freshdeskForm, repository: e.target.value })}
                      placeholder="Freshdesk KB"
                      className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-zinc-700 block mb-1">Folder Path</label>
                    <input
                      type="text"
                      value={freshdeskForm.folder_path}
                      onChange={(e) => setFreshdeskForm({ ...freshdeskForm, folder_path: e.target.value })}
                      placeholder="/freshdesk"
                      className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900"
                    />
                  </div>
                </div>

                {/* Scheduling */}
                <div className="pt-2 border-t border-zinc-100">
                  <label className="text-xs font-semibold text-zinc-700 block mb-2">Schedule Frequency</label>
                  <ScheduleFrequencyPicker
                    scheduleType={freshdeskForm.schedule_type}
                    scheduleValue={freshdeskForm.schedule_value}
                    onChange={(newType, newVal) =>
                      setFreshdeskForm({
                        ...freshdeskForm,
                        schedule_type: newType,
                        schedule_value: newVal
                      })
                    }
                  />
                </div>

                {/* Status toggle */}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs font-semibold text-zinc-700">Initial Status</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFreshdeskForm({
                        ...freshdeskForm,
                        status: freshdeskForm.status === "active" ? "inactive" : "active"
                      })
                    }
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                      freshdeskForm.status === "active"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-zinc-100 text-zinc-500 border border-zinc-200"
                    }`}
                  >
                    {freshdeskForm.status === "active" ? "Active" : "Disabled"}
                  </button>
                </div>
              </div>

              {/* Deduplication Guarantee note */}
              <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-100 text-[11px] text-zinc-500 flex items-start gap-2">
                <Shield size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                <span>
                  <strong>ID Deduplication Active:</strong> Freshdesk Article IDs are verified in EasyChat before fetching article bodies. Existing articles will not be re-downloaded or re-embedded.
                </span>
              </div>

              {/* Modal footer actions */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => setIsFreshdeskModalOpen(false)}
                  className="px-4 py-2 border border-zinc-200 text-zinc-700 rounded-xl text-xs font-semibold hover:bg-zinc-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800 transition-colors shadow-xs"
                >
                  {editingCron ? "Update Freshdesk Sync" : "Create Freshdesk Sync"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add / Edit Normal Modal */}
      {isModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-xs animate-fadeIn font-['Inter',sans-serif]">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-zinc-200 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-5 border-b border-zinc-100">
              <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                <Clock size={16} />
                {editingCron ? "Edit Cron Schedule" : "Add New Document Cron"}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4 overflow-y-auto">
              {/* Name */}
              <div>
                <label className="text-xs font-semibold text-zinc-700 block mb-1">Schedule Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Document Repository Sync"
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900"
                />
              </div>

              {/* API Endpoint */}
              <div>
                <label className="text-xs font-semibold text-zinc-700 block mb-1">
                  External API Endpoint (GET)
                </label>
                <input
                  type="url"
                  required
                  value={formData.api_url}
                  onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
                  placeholder="https://example.com/api/documents"
                  className="w-full px-3 py-2 text-xs font-mono border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900"
                />
                <p className="text-[11px] text-zinc-400 mt-1">
                  Must return document metadata list with <code className="text-zinc-600">id</code>,{" "}
                  <code className="text-zinc-600">name</code>, and <code className="text-zinc-600">url</code>.
                </p>
              </div>

              {/* Authentication Type */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-zinc-700 block mb-1">Authentication</label>
                  <CustomDropdown
                    options={[
                      { id: "none", name: "None (Public API)" },
                      { id: "bearer_token", name: "Bearer Token" },
                      { id: "api_key", name: "API Key (Header)" }
                    ]}
                    value={formData.authentication_type}
                    onChange={(val) => setFormData({ ...formData, authentication_type: val })}
                    buttonClassName="w-full px-3 py-2 border border-zinc-200 rounded-xl text-xs hover:border-zinc-900"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-zinc-700 block mb-1">HTTP Method</label>
                  <CustomDropdown
                    options={[
                      { id: "GET", name: "GET" },
                      { id: "POST", name: "POST" }
                    ]}
                    value={formData.http_method}
                    onChange={(val) => setFormData({ ...formData, http_method: val })}
                    buttonClassName="w-full px-3 py-2 border border-zinc-200 rounded-xl text-xs hover:border-zinc-900"
                  />
                </div>
              </div>

              {/* Credentials input if auth != none */}
              {formData.authentication_type !== "none" && (
                <div>
                  <label className="text-xs font-semibold text-zinc-700 block mb-1">
                    {formData.authentication_type === "bearer_token" ? "Bearer Token" : "API Key"}
                  </label>
                  <input
                    type="password"
                    value={formData.credentials}
                    onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
                    placeholder={
                      editingCron ? "Leave empty to keep existing encrypted secret" : "Paste secret token"
                    }
                    className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 font-mono"
                  />
                  <p className="text-[11px] text-zinc-400 mt-1 flex items-center gap-1">
                    <Shield size={11} className="text-emerald-600" />
                    Encrypted with Fernet on backend. Never visible in browser code or logs.
                  </p>
                </div>
              )}

              {/* Scheduling Options */}
              <div className="pt-2 border-t border-zinc-100">
                <label className="text-xs font-semibold text-zinc-700 block mb-2">Schedule Frequency</label>
                <ScheduleFrequencyPicker
                  scheduleType={formData.schedule_type}
                  scheduleValue={formData.schedule_value}
                  onChange={(newType, newVal) =>
                    setFormData({
                      ...formData,
                      schedule_type: newType,
                      schedule_value: newVal
                    })
                  }
                />
              </div>

              {/* Repository & Folder mapping */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-100">
                <div>
                  <label className="text-xs font-semibold text-zinc-700 block mb-1">Target Repository</label>
                  <input
                    type="text"
                    value={formData.repository}
                    onChange={(e) => setFormData({ ...formData, repository: e.target.value })}
                    placeholder="External Sync Repo"
                    className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-700 block mb-1">Folder Path</label>
                  <input
                    type="text"
                    value={formData.folder_path}
                    onChange={(e) => setFormData({ ...formData, folder_path: e.target.value })}
                    placeholder="/"
                    className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900"
                  />
                </div>
              </div>

              {/* Status toggle */}
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs font-semibold text-zinc-700">Initial Status</span>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      status: formData.status === "active" ? "inactive" : "active"
                    })
                  }
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    formData.status === "active"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-zinc-100 text-zinc-500 border border-zinc-200"
                  }`}
                >
                  {formData.status === "active" ? "Active" : "Disabled"}
                </button>
              </div>

              {/* Modal footer actions */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-zinc-200 text-zinc-700 rounded-xl text-xs font-semibold hover:bg-zinc-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800 shadow-xs"
                >
                  {editingCron ? "Update Cron" : "Save Cron"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Sync History Modal */}
      {isHistoryModalOpen && activeHistoryCron && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-xs animate-fadeIn font-['Inter',sans-serif]">
          <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl border border-zinc-200 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between p-5 border-b border-zinc-100">
              <div>
                <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                  <FileText size={16} />
                  Sync History: {activeHistoryCron.name}
                </h3>
                <p className="text-xs text-zinc-400 mt-0.5 truncate">{activeHistoryCron.api_url}</p>
              </div>
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-3">
              {loadingHistory ? (
                <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
                  <RefreshCcw size={20} className="animate-spin mb-2" />
                  <span className="text-xs">Fetching sync logs...</span>
                </div>
              ) : syncHistory.length === 0 ? (
                <div className="text-center py-12 text-zinc-400 text-xs">
                  No sync executions recorded yet.
                </div>
              ) : (
                syncHistory.map((item) => (
                  <div
                    key={item.id}
                    className="p-3.5 bg-zinc-50 border border-zinc-100 rounded-xl flex items-start justify-between gap-3 text-xs"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {item.status === "success" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs">
                            <CheckCircle2 size={13} />
                            {item.details || `${item.new_documents_count} new documents added`}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-600 font-semibold text-xs">
                            <XCircle size={13} />
                            {item.details || "API request failed"}
                          </span>
                        )}
                      </div>
                      {item.error_message && (
                        <p className="text-[11px] text-rose-500 font-mono leading-tight">
                          {item.error_message}
                        </p>
                      )}
                    </div>
                    <span className="text-[11px] text-zinc-400 whitespace-nowrap">
                      {formatDateDisplay(item.created_at)}
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-zinc-100 flex justify-end">
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="px-4 py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete Cron Schedule"
        message={`Are you sure you want to delete the Cron configuration "${cronToDelete?.name}"? Its sync history will also be removed.`}
      />
    </div>
  );
};

export default KnowledgeBaseCron;
